import ReportDeliverySettings from './ReportDeliverySettings'
import { useState } from 'react'
import SelfHealingTargets from './SelfHealingTargets'
import SelfHealingDialog from './SelfHealingDialog'
import SessionReviewerSettings from './SessionReviewerSettings'
import { CheckCircle2, CircleDashed } from 'lucide-react'
import { useGetMonitoringStatusQuery, useGetSelfHealingSessionsInfiniteQuery } from '../services/sessionMonitoringApi'
import ProjectMonitoringSetup from './ProjectMonitoringSetup'
import MonitoringSessionList from './MonitoringSessionList'
import './DailyMonitoringSessions.css'
import './SetupPage.css'

export default function SelfHealingSessions({ projectId }: { projectId: string }) {
  const [dialog, setDialog] = useState<'settings' | null>(null)
  const status = useGetMonitoringStatusQuery(projectId, { pollingInterval: 15000, skipPollingIfUnfocused: true })
  const connected = status.currentData?.has_sessions === true
  const query = useGetSelfHealingSessionsInfiniteQuery(projectId, { skip: !connected, pollingInterval: 15000, skipPollingIfUnfocused: true })
  const pages = query.currentData?.pages
  const sessions = [...new Map(pages?.flatMap(page => page.sessions).map(session => [session.session_id, session])).values()]
  return <section className="self-healing daily-monitoring-sessions setup-stack testing-settings" aria-label="User sessions">
    <section className="setup-section" aria-labelledby="self-healing-connection-title">
      <header className="setup-section__header">
        <div><h2 id="self-healing-connection-title">Connect your app</h2>
          <p>{connected ? 'Explore captured sessions, review findings, and follow reproduction journeys.' : 'Install the capture package so QA can review real user sessions and investigate bugs.'}</p></div>
        <div className="setup-section__actions">
          <button type="button" onClick={() => setDialog('settings')}>Reviewer Settings</button>
        </div>
      </header>
      <div className="setup-section__body">
        {status.isLoading && <p role="status">Checking connection…</p>}
        {status.isError && <p role="alert">Could not check the session connection. <button onClick={() => void status.refetch()}>Retry</button></p>}
        {status.currentData && <p className={`setup-status ${connected ? 'setup-status--success' : 'setup-status--waiting'}`} role="status">
          {connected ? <CheckCircle2 size={16} aria-hidden /> : <CircleDashed size={16} aria-hidden />}
          <strong>{connected ? 'Sessions received' : 'No sessions recorded, waiting…'}</strong>
          {!connected && <button type="button" disabled={status.isFetching} onClick={() => void status.refetch()}>{status.isFetching ? 'Checking…' : 'Check now'}</button>}
        </p>}
        {status.currentData?.can_edit && (connected ? <details><summary>Setup instructions</summary><ProjectMonitoringSetup projectId={projectId} /></details> : <ProjectMonitoringSetup projectId={projectId} />)}
      </div>
    </section>
    {dialog && <SelfHealingDialog title="Reviewer Settings" onClose={() => setDialog(null)}>
      <SessionReviewerSettings projectId={projectId} />
    </SelfHealingDialog>}
    <section className="setup-section" aria-labelledby="self-healing-limit-title">
      <header className="setup-section__header">
        <div><h2 id="self-healing-limit-title">Daily session limit</h2><p>Caps how many sessions QA reviews each day, and so how many credits Self Healing uses.</p></div>
      </header>
      <div className="setup-section__body"><SelfHealingTargets projectId={projectId} /></div>
    </section>
    <ReportDeliverySettings projectId={projectId} />
    {connected && <section className="setup-section" aria-labelledby="self-healing-sessions-title">
      <header className="setup-section__header">
        <div><h2 id="self-healing-sessions-title">User sessions{pages ? ` · ${pages[0]!.total.toLocaleString()}` : ''}</h2><p>The most recent sessions QA received from your app.</p></div>
      </header>
      <div className="setup-section__body">
        {query.isLoading && <p role="status">Loading user sessions…</p>}
        {query.isError && <p role="alert">Could not load user sessions. <button onClick={() => void query.refetch()}>Retry</button></p>}
        {pages && <MonitoringSessionList readOnly projectId={projectId} sessions={sessions} canEdit={pages[0]!.can_edit} onRefresh={query.refetch} />}
        {query.hasNextPage && <div className="daily-monitoring-sessions__pagination"><button type="button" disabled={query.isFetching} onClick={() => void query.fetchNextPage()}>{query.isFetchingNextPage ? 'Loading…' : 'Load more'}</button></div>}
      </div>
    </section>}
  </section>
}
